import type {
  BaseAttendanceDto,
  BaseGambitDto,
  GambitFormData,
  GambitRecurrenceOptions,
  SparkGambitOptions,
} from '@/core/data/gambit/gambit.interface';
import { createLogger } from '@/core/helpers/logger.helper';
import type {
  GambitApi,
  LiveGambitDto,
  OutgoingInvitationDto,
  RespondGambitDto,
} from '@/core/network/api/gambit/gambit.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HandleApiError, HttpError } from '@/core/network/http/httpError';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GambitSearchResultDto } from '@/views/composables/models/components/GambitSearchResultDto';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import type { GeoCoords } from '../location/location.interface';
import type { GambitInvitationType } from './gambit.type';

const logger = createLogger('GambitRepository');

export class GambitRepository {
  private readonly httpAPI: GambitApi;

  constructor(httpAPI: GambitApi) {
    this.httpAPI = httpAPI;
  }

  async findGambitResults(
    locationIds: string[],
    start: string | Date,
    end: string | Date,
  ): Promise<GamResponse<GambitSearchResultDto[]> | null> {
    try {
      const response = await this.httpAPI.findGambitResults(locationIds, start, end);
      return response.data?.data ? { data: response.data.data, cursor: response.data.cursor } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async findMyGambits(start: string | Date, end: string | Date): Promise<GamResponse<GambitSearchResultDto[]> | null> {
    try {
      const response = await this.httpAPI.findMyGambits(start, end);
      return response.data?.data ? { data: response.data.data, cursor: response.data.cursor } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getGambits(listId: GamListId, location: GeoCoords): Promise<GamResponse<BaseGambitDto[]> | null> {
    try {
      const response = await this.httpAPI.getGambits(listId, location);
      return response.data?.data ? { data: response.data.data, cursor: response.data.cursor } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getGambit(id: string, location?: GeoCoords): Promise<GamResponse<GambitDetailDto> | null> {
    try {
      const response = await this.httpAPI.getGambit(id, location);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async addNewGambit(data: GambitFormData): Promise<GamResponse<BaseGambitDto> | null> {
    try {
      const response = await this.httpAPI.addGambit(data);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createFirestoreDoc(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createFirestoreDoc(id);
      return response.status === 200 ? response.data || { data: response.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateGambit(
    id: string,
    data: GambitFormData,
    options?: GambitRecurrenceOptions,
  ): Promise<GamResponse<BaseGambitDto | { status: number }> | null> {
    try {
      const response = await this.httpAPI.updateGambit(id, data, options);
      return response.status === 200 ? response.data || { data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateChallenge(
    id: string,
    data: Partial<GambitFormData>,
  ): Promise<GamResponse<{ message: string; gambitId?: string }> | null> {
    try {
      const response = await this.httpAPI.updateChallenge(id, data);

      return response.status === 201
        ? { data: { message: response.data.data?.message || '', gambitId: response.data.data?.gambitId } }
        : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteGambit(id: string, options?: GambitRecurrenceOptions): Promise<GamResponse<BaseGambitDto> | null> {
    try {
      const response = await this.httpAPI.deleteGambit(id, options);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async joinSpark(data: SparkGambitOptions): Promise<GamResponse<BaseGambitDto> | null> {
    try {
      const response = await this.httpAPI.joinSpark(data);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createAttendance(
    id: string,
    options?: { recurrences?: boolean },
  ): Promise<GamResponse<BaseAttendanceDto> | null> {
    try {
      const response = await this.httpAPI.createAttendance(id, options);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateAttendance(
    gambitId: string,
    chessSetCount: number,
    status?: string,
  ): Promise<GamResponse<BaseAttendanceDto> | null> {
    try {
      const response = await this.httpAPI.updateAttendance(gambitId, chessSetCount, status);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteAttendance(
    id: string,
    options?: { recurrences?: boolean },
  ): Promise<GamResponse<BaseAttendanceDto> | null> {
    try {
      const response = await this.httpAPI.deleteAttendance(id, options);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createInvitations(
    gambitId: string,
    userIds: string[],
    type: GambitInvitationType,
  ): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createInvitations(gambitId, userIds, type);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async acceptChallenge(gambitId: string): Promise<GamResponse<{ gambitId: string }> | null> {
    try {
      const response = await this.httpAPI.acceptChallenge(gambitId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async declineInvitation(gambitId: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.declineInvitation(gambitId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async withdrawChallenge(gambitId: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.withdrawChallenge(gambitId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getRespondInvitations(): Promise<GamResponse<RespondGambitDto[]> | null> {
    try {
      const response = await this.httpAPI.getRespondInvitations();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getOutgoingInvitations(userId: string): Promise<GamResponse<OutgoingInvitationDto[]> | null> {
    try {
      const response = await this.httpAPI.getOutgoingInvitations(userId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getLiveGambits(): Promise<GamResponse<LiveGambitDto[]> | null> {
    try {
      const response = await this.httpAPI.getLiveGambits();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async dismissRecentGambit(gambitId: string): Promise<GamResponse<boolean> | null> {
    try {
      const response = await this.httpAPI.dismissRecentGambit(gambitId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async cancelGambitInvitation(gambitId: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.cancelGambitInvitation(gambitId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }
}
